import {forwardRef,useImperativeHandle,useCallback,useMemo} from 'react';
import {useEffect,useState} from "react";
import {Box,Typography,Button,ButtonGroup} from '@mui/material';
import {useNavigate,useLocation} from 'react-router-dom';
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext';
import {Stack} from '@mui/material';
import {consoleLog,compressSingleFields,filterPageTabs} from '../../utilities/Utilities';
import {setObjectFieldValue,getObjectRecord,upsertObjectRecord,invokeObjectMethod,preCreateObjectRecord,deleteObjectRecordsByIdList} from '../../integration/ObjectManager.js';
import COMPONENT_MAP from '../Repository/Components.js';
import RecordInfo from '../RecordInfo.js';
import Popup from './Popup.js';
import CollapsibleSection from './CollapsibleSection.js';
import {useConfirm} from '../ConfirmContext.js';
import {useNotifications} from '../Notification/NotificationsContext.js'
import {getStringLang} from '../../utilities/Strings.js';

let pendingTransactions=false;
let pendingSave=false;
let noCommitPending=false;
let postCommitArgsPending={};
const ISMOBILE=window.matchMedia('(max-width: 768px)').matches;

const PageDetail=forwardRef(({userData,application,entityType,entityParams,file,titleLabel,recordInfo={show:false,errorLog:false},titleFieldName,pageTabs,defautPageTabPath,pageCallback,navigateToOnCreation,isPopUp=false,recordId,fromPopup=false,hideParent=false,openOnUpdate=false,extraPopupContent},ref)=>{
    const [singleFields,setSingleFields]=useState({});
    const [multivalueFields,setMultivalueFields]=useState({});
    const [joinFields,setJoinFields]=useState({});
    const [rowId,setRowId]=useState(null);
    const [recordRules,setRecordRules]=useState({});
    const [rulesRecordId,setRulesRecordId]=useState(null);//record the current recordRules were loaded for
    const [entityRules,setEntityRules]=useState({});
    const navigate=useNavigate();
    const [isChanged,setIsChanged]=useState(false);
    const [readOnly,setReadOnly]=useState(true);
    const location=useLocation();
    const [pageTab,setPageTab]=useState(()=>{
        if(pageTabs&&pageTabs.length>0){
            const index=pageTabs.findIndex(tab=>location.pathname.includes(tab.path));
            return index>=0?index:0;
        }
        return 0;
    });
    const {showSnackbar}=useSnackbar();
    const {showLoading,hideLoading}=useLoading();
    const [showMoreInfo,setShowMoreInfo]=useState(false);
    const [messagePopup,setMessagePopup]=useState({show:false,mesage:''});
    const [recordInfoPopup,setRecordInfoPopup]=useState({show:false,errorLog:false});
    const [pageTabsRefreshToken,setPageTabsRefreshToken]=useState(0);
    const {confirm}=useConfirm();
    const {refreshNotifications}=useNotifications();

    useEffect(()=>{//When the component is loaded, if "Slug" exists, then it is editing. Otherwise it is a new record
        if(entityParams){
            if(recordId){
                getRecord();//get existing record
                if(location.state?.operationMessage){
                    setMessagePopup({show:true,message:location.state.operationMessage});
                }
            }
            else{
                preCreateRecord();//new record preparation
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[recordId]);

    //Role-filtered tabs from the menu config, minus the ones the backend hides for this record
    //(ignored while switching records, until the new record's rules arrive)
    const hiddenPageTabs=rulesRecordId===recordId?recordRules?.hidden_page_tabs:undefined;
    const visiblePageTabs=useMemo(()=>filterPageTabs(pageTabs,hiddenPageTabs),[pageTabs,hiddenPageTabs]);

    const tabSegmentIndex=recordId?3:2;//position of the tab (":context") in the URL path

    useEffect(()=>{//keep the selected tab index in sync with the URL and with the visible tabs
        if(!visiblePageTabs||visiblePageTabs.length===0) return;
        const segments=location.pathname.split('/');
        const currentTab=segments[tabSegmentIndex];
        const index=visiblePageTabs.findIndex(tab=>tab.path===currentTab||tab.name===currentTab);
        if(index>=0){
            setPageTab(index);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[visiblePageTabs,location.pathname]);

    //Deep link to a tab the backend hides for this record - same answer as a tab that doesn't exist
    //(PrivateRoute's "Invalid Page or Access not Allowed"), instead of rendering a tab index that's gone
    const currentTabHidden=!isPopUp&&!!recordId&&rulesRecordId===recordId&&!!hiddenPageTabs?.includes(location.pathname.split('/')[tabSegmentIndex]);

    const getRecord=()=>{//get existing record by id
        setReadOnly(true);
        getObjectRecord({entityType:entityType,entityId:recordId,entityParams:entityParams})
        .then((data)=>{
            consoleLog({file:file,context:'getRecord - getObjectRecord',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            else if(data.record){
                setSingleFields(data.record.single_fields);
                setMultivalueFields(data.record.multivalue_fields);
                setJoinFields(data.record.join_fields)
                setRecordRules(data.record_rules);
                setRulesRecordId(recordId);
                setEntityRules(data.entity_rules);
                setRowId(data.record.single_fields.id.value);
                pageCallback('recordLoaded',{singleFields:data.record.single_fields});//if needed, more arguments can be passed

                if(data.record_rules.can_update_record&&openOnUpdate){
                    handleClickEvent({action:'update'});
                }
            }
            else{
                showSnackbar(application.getLocale("RecordNotFoundOrNotAccessible"),'error');
            }
        });
    };

    const preCreateRecord=()=>{//generate metadata template for the new record
        preCreateObjectRecord({entityType:entityType,entityParams:entityParams})
        .then((data)=>{
            consoleLog({file:file,context:'preCreateRecord - preCreateObjectRecord',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            else if(data.record){
                setIsChanged(true);
                setReadOnly(false);
                setSingleFields(data.record.single_fields);
                setMultivalueFields(data.record.multivalue_fields);
                setJoinFields(data.record.join_fields);
                setRecordRules(data.record_rules);
            }
            else{
                showSnackbar(application.getLocale("RecordNotFoundOrNotAccessible"),'error');
            }
        });
    };

    const setFieldValue=(fieldName,fieldValue)=>{//api is called for each field change to validate and reload rules from backend
        const record={
            'single_fields':compressSingleFields(singleFields),
            'join_fields':joinFields,
            'multivalue_fields':multivalueFields,
        }
        return setObjectFieldValue({entityType:entityType,record:record,set_param:{field:fieldName,value:fieldValue},_lang:application.globalData.lang})
        .then((data)=>{
            consoleLog({file:file,context:'setFieldValue - setObjectFieldValue',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            else{
                if(pendingTransactions){
                    pendingTransactions=false;
                }

                if(recordId){
                    setIsChanged(true);
                    setRecordRules(data.record_rules);
                    //setEntityRules(data.entity_rules);
                }
                else if(data.record_rules){//New record: only models opting in (__dynamic_new_form__) send rules back - layout follows the values typed
                    setRecordRules(data.record_rules);
                }

                const editedField=data.record?.single_fields?.[fieldName];
                if(editedField?.validation_error){
                    //A cross-field business rule (e.g. Event's end_time <= start_time) rejected
                    //this specific field's new value - the model's @validates never lets a rejected
                    //assignment land on the record, so the backend's response reports this field's
                    //OLD, unchanged value back, not what was just typed. Blindly applying that here
                    //would silently swap the field back to its old value while still flagging it as
                    //an error - confusing - and would also mean the next Save has nothing invalid
                    //left to actually catch, since this field's state would already be back to a
                    //valid value by then. Keep what the user attempted for this one field (record_
                    //rules-driven flags on every field, including this one's error decoration,
                    //still refresh normally) so it stays visible and Save's own validation
                    //(upsertRecord/pre_commit_operations) is the one that gets the final say on it.
                    setSingleFields({
                        ...data.record.single_fields,
                        [fieldName]:{...editedField,value:fieldValue}
                    });
                }
                else{
                    setSingleFields(data.record.single_fields);
                }
            }

            return data;
        });
    };

    useEffect(()=>{
        if(pendingSave){
            consoleLog({file:file,context:'useEffect',arg_name:'pendingSave',arg_value:pendingSave});
            consoleLog({file:file,context:'useEffect',arg_name:'ISMOBILE',arg_value:ISMOBILE});
            upsertRecord(noCommitPending,postCommitArgsPending);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[singleFields,multivalueFields]);

    const upsertRecord=(no_commit=false,context={})=>{//save or create record
        consoleLog({file:file,context:'upsertRecord',arg_name:'ISMOBILE',arg_value:ISMOBILE});
        consoleLog({file:file,context:'upsertRecord',arg_name:'pendingTransactions',arg_value:pendingTransactions});
        consoleLog({file:file,context:'upsertRecord',arg_name:'no_commit',arg_value:no_commit});
        consoleLog({file:file,context:'upsertRecord',arg_name:'context',arg_value:context});
        if(!pendingTransactions){
            pendingSave=false;
            showLoading();
            const record={
                'single_fields':compressSingleFields(singleFields),
                'join_fields':joinFields,
                'multivalue_fields':multivalueFields,
            }
            consoleLog({file:file,context:'upsertRecord',arg_name:'record',arg_value:record});
            upsertObjectRecord({entityType:entityType,record:record,_lang:application.globalData.lang,context:context,no_commit:no_commit})
            .then((data)=>{
                consoleLog({file:file,context:'upsertRecord - upsertObjectRecord',arg_name:'data',arg_value:data});
                if('error' in data){
                    showSnackbar(data.error,'error');
                }
                else{
                    if(recordId){
                        let isPopupError=false;
                        if(data?.header_validation?.error){
                            isPopupError=true;
                            showSnackbar(data?.header_validation?.message,'error');
                        }
                        else{
                            if(data?.operation_message){
                                setMessagePopup({show:true,message:data.operation_message});
                            }
                            else{
                                showSnackbar(application.getLocale("RecordUpdatedWithSuccess"),'success');//fix to generic
                            }

                            if(!isPopUp){
                                setIsChanged(false);
                                setReadOnly(true);
                                setRowId(null);
                            }
                        }
                        if(!isPopUp){
                            setSingleFields(data.record.single_fields);
                            setJoinFields(data.record.join_fields);
                            setMultivalueFields(data.record.multivalue_fields);
                            setRecordRules(data.record_rules);
                            //setEntityRules(data.entity_rules);
                            setTimeout(()=>{setRowId(data.record.single_fields.id.value);},100);
                            pageCallback('postUpsertRecord',{singleFields:singleFields});//if needed, more arguments can be passed
                            hideLoading();
                        }
                        else{
                            hideLoading();
                            if(isPopupError){
                                setSingleFields(data.record.single_fields);
                            }
                            else{
                                pageCallback('refreshParent',{close:true,id:data.record.single_fields.id.value});
                            }
                        }
                    }

                    else{
                        if(data?.header_validation?.error){
                            showSnackbar(data?.header_validation?.message,'error');
                            setSingleFields(data.record.single_fields);
                            setMultivalueFields(data.record.multivalue_fields);
                            setJoinFields(data.record.join_fields);
                            hideLoading();
                        }
                        else{
                            if(no_commit){
                                hideLoading();
                                pageCallback('upsertRecord',{singleFields:singleFields});
                            }
                            else{
                                showSnackbar(application.getLocale("RecordCreatedWithSuccess"),'success');
                                if(!isPopUp){
                                    hideLoading();
                                    const url=navigateToOnCreation.replace("{ENTITY_ID}", data.record.single_fields.id.value);
                                    navigate(url,{state:{operationMessage:data.operation_message||null,},});
                                }
                                else{
                                    hideLoading();
                                    if(data?.operation_message){//the popup closes - show the notice as a snackbar instead of the success one
                                        showSnackbar(data.operation_message,'warning');
                                    }
                                    pageCallback('refreshParent',{close:true,id:data.record.single_fields.id.value});
                                }
                            }
                        }
                    }
                }
            });
        }
        else{
            pendingSave=true;
            noCommitPending=no_commit;
            postCommitArgsPending=context;
        }
    }

    const invokeMethod=async(method)=>{//call specialized actions on backend - it will be called from parent component
        showLoading();
        const {recordFields,rowId,...args}=method.args;
        method.args=args;
        const record={
            'single_fields':compressSingleFields(singleFields),
            'join_fields':joinFields,
            'multivalue_fields':multivalueFields,
        }
        consoleLog({file:file,context:'invokeMethod',arg_name:'method',arg_value:method});
        const data=await invokeObjectMethod({entityType:entityType,entityId:recordId,record:record,entityMethod:method.name,args:method.args,_lang:application.globalData.lang});
        consoleLog({file:file,context:'invokeMethod - invokeObjectMethod',arg_name:'data',arg_value:data});
        if('error' in data){
            setRowId(null);
            setSingleFields(data.record.single_fields);
            setJoinFields(data.record.join_fields);
            setMultivalueFields(data.record.multivalue_fields);
            setRecordRules(data.record_rules);
            setRowId(data.record.single_fields.id.value);
            if(data?.args?.refresh_page_tab===true){
                setPageTabsRefreshToken(v=>v+1);
            }
            showSnackbar(data.error,'error');
        }
        else{
            setRowId(null);
            if(data?.message){
                showSnackbar(data?.message,'success');
            }
            if(data?.args?.set_is_changed===true){
                setIsChanged(true);
            }
            if(data?.args?.refresh_page_tab===true){
                setPageTabsRefreshToken(v=>v+1);
            }
            setSingleFields(data.record.single_fields);
            setJoinFields(data.record.join_fields);
            setMultivalueFields(data.record.multivalue_fields);
            setRecordRules(data.record_rules);
            //setEntityRules(data.entity_rules);
            setRowId(data.record.single_fields.id.value);
        }
        hideLoading();
        return data;
    }

    const deleteRecord=(idList)=>{
        idList=[rowId];
        consoleLog({file:file,context:'deleteRecord',arg_name:'idList',arg_value:idList});
        showLoading();
        deleteObjectRecordsByIdList({entityType:entityType,entityParams:['idList='+idList]})
        .then((data)=>{
            consoleLog({file:file,context:'deleteRecord - deleteObjectRecordsByIdList',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
                hideLoading();
            }
            else{
                showSnackbar(application.getLocale("RecordRemovedSuccess"),'success');
                hideLoading();
                if(entityType==='Notifications'){
                    refreshNotifications();
                }
                const fallback=entityRules?.go_to_after_detail_record_delete;
                const url=fallback||location.state?.from||"/";
                navigate(url);
            }
        });
    };

    const handleClickMethodButton=async(method)=>{//when the method is clicked, the parent component will perform "PreInvoke" actions and then back to call the method
        consoleLog({file:file,context:'handleClickMethodButton',arg_name:'method',arg_value:method});
        const mergedArgs={
            ...(method.args||{}),//args coming from backend
            rowId:recordId,//standard args
            recordFields:{//used when rowId is null, record is not saved yet - Object "New" page can check fields already populated for context
                'single_fields':singleFields,
                'join_fields':joinFields,
                'multivalue_fields':multivalueFields,
            }
        };
        await pageCallback('invokeMethod',{
            name:method.name,
            args:mergedArgs,
        });
    };

    useImperativeHandle(ref,()=>({//allow functions to be called from parent component
        invokeMethod,refreshEntity,upsertRecord,handleClickEvent,refreshData
    }));

    const handleShowMoreInfo=()=>{
        setShowMoreInfo(!showMoreInfo);
    }

    const handleChangeTab=(event,newValue)=>{
        event.currentTarget.blur();
        setPageTab(newValue);
        navigate(location.pathname.replace(location.pathname.split('/')[tabSegmentIndex],visiblePageTabs[event.target.name].path));
    };
    
    const setFieldChanged=useCallback(()=>{
        if(recordId&&!isChanged) setIsChanged(true);
        if(!pendingTransactions) pendingTransactions=true;
    },[recordId,isChanged]);

    //multi-value fields don't go through setFieldValue's backend round-trip, so they must not set
    //pendingTransactions - otherwise it never gets cleared and upsertRecord() silently queues the save forever
    const setMultiFieldChanged=useCallback(()=>{
        if(recordId&&!isChanged) setIsChanged(true);
    },[recordId,isChanged]);

    const refreshEntity=()=>{
        consoleLog({file:file,context:'refreshEntity',arg_name:null,arg_value:null});
        setRowId(null);
        getRecord();
    }

    const refreshData=()=>{
        consoleLog({file:file,context:'refreshData',arg_name:null,arg_value:null});
        getRecord();
    }

    const handleClickEvent=async(args)=>{
        consoleLog({file:file,context:'handleClickEvent',arg_name:'args',arg_value:args});
        switch(args?.action){
            case "update":
                setReadOnly(false);
                break;
            case "delete":
                if(await confirm({message:application.getLocale("ConfirmRecordDeletion")})){
                    deleteRecord();
                }
                break;
            case "save":
                pageCallback('preUpsertRecord',{singleFields:singleFields});
                break;
            case "save_no_commit"://trigger validations only
                upsertRecord(true,args?.context??{});
                break;
            case "cancel":
                if(recordId){
                    pendingTransactions=false;
                    setIsChanged(false);
                    getRecord();
                }
                else{
                    if(!isPopUp){
                        navigate(location.state?.from||-1);
                    }
                    else{
                        pageCallback('closePopup',{});
                    }
                }
                break;
            case "refresh":
                refreshEntity();
                break;
            case "open_record_info":
                setRecordInfoPopup({show:true,errorLog:args.errorLog});
                break;
            case "close_record_info":
                setRecordInfoPopup({show:false,errorLog:false});
                break;
            case "save_nopre":
                upsertRecord(false,args?.context??{});
                break;
            case "save_popup":
                upsertRecord(false,args?.context??{});
                break;
            case "close_popup":
                pageCallback('refreshParent',{close:true});
                break;
            default:
                break;
        }
    }

    const renderSingleField=useCallback((fieldName)=>{
        const Component=COMPONENT_MAP[singleFields[fieldName]?.ui_render_component];
        return Component?(
            <Component
                key={fieldName}
                row={singleFields}
                fieldName={fieldName}
                entityType={entityType}
                application={application}
                recordRules={recordRules}
                readOnly={readOnly}
                setFieldChanged={setFieldChanged}
                setFieldValue={setFieldValue}
                refreshData={refreshData}
                userData={userData}
            />
        ):null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[singleFields,recordRules,readOnly]);

    const renderLabelText=(fieldName)=>{
        const Component=COMPONENT_MAP['LabelText'];
        return Component?(
            <Component
                key={fieldName}
                row={singleFields}
                fieldName={fieldName}
                application={application}
            />
        ):null; 
    };

    const renderFieldStaticValue=(fieldName)=>{
        const Component=COMPONENT_MAP['FieldStaticValue'];
        return Component?(
            <Component
                key={fieldName}
                row={singleFields}
                fieldName={fieldName}
                application={application}
            />
        ):null; 
    };
    
    const renderMethodButton=(method_args)=>{
        const shouldShow=(method_args.onReadOnlyInvoke===true)?!readOnly:readOnly;
        if(!shouldShow) return null;
        return <Box key={method_args.name} sx={{marginBottom:'10px !important'}}>
            <ButtonGroup key={method_args.name} size="small">
                <Button
                    key={method_args.name}
                    color={method_args.color}
                    name={method_args.name}
                    variant="outlined"
                    onClick={()=>handleClickMethodButton(method_args)}>
                    {application.getLocale(method_args?.label)}
                </Button>
            </ButtonGroup>
        </Box>
    };

    const renderMultiValueField=(fieldName)=>{
        const Component=COMPONENT_MAP[multivalueFields[fieldName]?.ui_render_component];
        return Component?(
            <Component
                key={fieldName}
                fieldName={fieldName}
                application={application}
                recordRules={recordRules}
                readOnly={readOnly}
                setFieldChanged={setMultiFieldChanged}
                multivalueFields={multivalueFields}
                setMultivalueFields={setMultivalueFields}
            />
        ):null;
    };

    const renderJoinField=(JoinfieldName)=>{
        const [joinName,fieldNameJoin]=JoinfieldName.split(".");
        const Component=COMPONENT_MAP[joinFields[joinName][fieldNameJoin]?.ui_render_component];
        return Component?(
            <Component
                key={JoinfieldName}
                fieldNameJoin={fieldNameJoin}
                application={application}
                row={singleFields}
                rowJoin={joinFields[joinName]}
                recordRules={recordRules}
                userData={userData}
                readOnly={readOnly}
                setFieldValue={setFieldValue}
                setFieldChanged={setFieldChanged}
            />
        ):null; 
    };

    const renderJoinFieldBarcode=(JoinfieldName)=>{
        const [joinName,fieldNameJoin]=JoinfieldName.split(".");
        const Component=COMPONENT_MAP['FieldBarcode'];
        return Component?(
            <Component
                key={JoinfieldName+'_barcode'}
                value={joinFields[joinName][fieldNameJoin]?.value}
                displayValue={false} 
                height={20}
                width={2}
            />
        ):null; 
    };

    const renderButtonShowMoreInfo=()=>{
        const Component=COMPONENT_MAP['ButtonShowMoreInfo'];
        return Component?(
            <Component
                showMoreInfo={showMoreInfo}
                handleShowMoreInfo={handleShowMoreInfo}
                application={application}
            />
        ):null; 
    };

    const renderPageHeader=()=>{
        const Component=COMPONENT_MAP['PageHeader'];
        return Component?(
            <Component
                title={<>{titleLabel}<span className="body-title">{singleFields?.[titleFieldName]?.value}</span></>}
            />
        ):null; 
    };

    const pageTabCallback=(mode,args)=>{
        if(mode==='refresh'){
            refreshEntity();
        }
        else if(mode==='refresh_data'){
            refreshData();
        }
        else if(mode==='invoke_method'){
            invokeMethod(args);
        }
    }

    const renderPageDetailTabs=useCallback(()=>{
        const Component=COMPONENT_MAP['PageDetailTabs'];
        if(recordId&&rulesRecordId!==recordId) return null;//wait for the record (and its hidden_page_tabs) so hidden tabs don't flash
        return (visiblePageTabs!==null&&visiblePageTabs!==undefined)&&Component?(
            <Component
                pageTabs={visiblePageTabs}
                pageTab={pageTab}
                application={application}
                handleChangeTab={handleChangeTab}
                rowId={rowId}
                row={singleFields}
                userData={userData}
                entityType={entityType}
                pageTabCallback={pageTabCallback}
                refreshToken={pageTabsRefreshToken}
            />
        ):null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[pageTab,rowId,rulesRecordId,visiblePageTabs,application.globalData.dataGridParams,pageTabsRefreshToken]);

    const renderFieldGroups=useCallback((fieldGroups)=>{ 
        return fieldGroups?.map((line,index)=>{
            const lineKey=line&&Object.keys(line)?.[0];
            const lineFields=lineKey?line[lineKey]:null;
            if(typeof lineKey==="string"&&lineKey.includes("section")){
                return (
                    <CollapsibleSection
                        key={lineKey+index}
                        title_label={application.getLocale(lineFields?.section_title_label)}
                        expanded={lineFields?.section_default_expanded}>
                        {renderFieldGroups(lineFields?.section_fields)}</CollapsibleSection>);
            }
            if(!Array.isArray(lineFields)){
                return null;
            }
            else{
                return(
                <Stack key={(lineKey??'line')+index} direction={{xs:'column',sm:'row'}} spacing={{xs:1,sm:2,md:2}} sx={{mb:isPopUp?1:0}}>
                {lineFields.map(({type,field,args})=>{
                switch (type) {
                    case 'single':
                        return renderSingleField(field);
                    case 'join':
                        return renderJoinField(field);
                    case 'join_barcode':
                        return renderJoinFieldBarcode(field);
                    case 'multi':
                        return renderMultiValueField(field);
                    case 'label':
                        return renderLabelText(field);
                    case 'static':
                        return renderFieldStaticValue(field);
                    case 'method_button':
                        return renderMethodButton(args);
                    case 'empty':
                        //A pure layout spacer - takes the same flex:1 share of the row as every
                        //other field component (FieldSwitch/FieldText/etc all wrap themselves in an
                        //identical Box) so a line with fewer fields than its neighbors can still
                        //line up into the same column grid instead of stretching unevenly. No
                        //lookup against singleFields - field is only used as this entry's React key.
                        return <Box key={field||'empty'} sx={{flex:1,minWidth:0}}/>;
                    default:
                        return null;
                }
                })}
            </Stack>)}
        })??[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[renderSingleField,renderJoinField,renderMultiValueField,renderLabelText,renderFieldStaticValue]);

    const renderedFieldsBefore=useMemo(()=>renderFieldGroups(recordRules?.form_config?.bodyBeforeMoreInfo),[recordRules,renderFieldGroups]);
    const renderedFieldsAfter=useMemo(()=>renderFieldGroups(recordRules?.form_config?.bodyAfterMoreInfo),[recordRules,renderFieldGroups]);

    const renderDetailFormPopup=()=>{
        const Component=COMPONENT_MAP['DetailFormPopup'];
        return Component?(
            <Component
                key={'DetailFormPopup'}
                title={titleLabel}
                application={application}
                handleClickEvent={handleClickEvent}
                isChanged={isChanged}
                componentBody={<>{renderedFieldsBefore}{extraPopupContent?.(rowId,singleFields)}</>}
                readOnly={readOnly}
                recordRules={recordRules}
                fromPopup={fromPopup}
                openOnUpdate={openOnUpdate}
                handleClickMethodButton={handleClickMethodButton}
            />
        ):null;
    };

    const renderDetailForm=()=>{
        const Component=COMPONENT_MAP['DetailForm'];
        return Component?(
            <Component
                key={recordId??'DetailForm'}
                renderPageHeader={renderPageHeader}
                readOnly={readOnly}
                recordRules={recordRules}
                handleClickEvent={handleClickEvent}
                renderedFieldsBefore={renderedFieldsBefore}
                renderButtonShowMoreInfo={renderButtonShowMoreInfo}
                recordId={recordId}
                recordInfo={recordInfo}
                showMoreInfo={showMoreInfo}
                renderedFieldsAfter={renderedFieldsAfter}
                renderPageDetailTabs={renderPageDetailTabs}
                handleClickMethodButton={handleClickMethodButton}
                application={application}
                isChanged={isChanged}
                hideParent={hideParent}
            />
        ):null; 
    };

    if(currentTabHidden){
        return <div>{getStringLang('InvalidPageOrAccessNotAllowed',application?.globalData?.lang||'en')}</div>;
    }

    return (
        <>
            {messagePopup.show&&
            <Popup
                title={application.getLocale("LabelError")}
                titleBackgroundColor={'darkred'}
                footerComponent={<Box sx={{display:'flex',justifyContent:'flex-end',mt:3}}>
                    <Button sx={{color:'darkred'}} onClick={()=>{setMessagePopup({show:false,message:false})}}>{application.getLocale("ButtonClose")}</Button>
                </Box>}
                onClose={()=>setMessagePopup({show:false,message:false})}>
                <Box
                    sx={{
                        display:'flex',
                        alignItems:'center',
                        justifyContent:'center',
                        color:'darkred'
                    }}>
                    <Typography variant="h6">{messagePopup.message}</Typography>
                </Box>
            </Popup>}
            {recordInfoPopup.show&&<RecordInfo
                popupCallBack={handleClickEvent}
                rowId={rowId}
                errorLog={recordInfoPopup.errorLog}
                singleFields={singleFields}
                userData={userData}
                application={application}
                entityType={entityType}
            />}

            {isPopUp&&renderDetailFormPopup()}
            {!isPopUp&&renderDetailForm()}
        </>
    );
});

export default PageDetail;
